import { X, ArrowLeft } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import logger from '@/utils/logger'
import {
  bussinessSignInFormControls,
  // bussinessSignUpFormControls,
  userSignInFormControls,
  userSignUpFormControls,
} from '../../../config'
import { AuthContext } from '../../../contexts/auth-context'
import CommonForm from '../../common-form'
import OtpVerification from '../../otp-verification/OtpVerification'
import ForgotPassword from '../forgot-password/ForgotPassword'
import { getConfig } from '@/services'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const MAIN_TABS = ['user', 'businesses']
const SPECIAL_TABS = ['otp-verify', 'forgot-password']

function AuthModal() {
  const [prevMainTab, setPrevMainTab] = useState('user')
  const [config, setConfig] = useState([])
  const [configValue, setConfigValue] = useState()

  const {
    setOtp,
    activeTab,
    setActiveTab,
    setIsLoginModalOpen,
    userSignInFormData,
    accountAlreadyCreated,
    setAccountAlreadyCreated,
    setUserSignInFormData,
    bussinessSignInFormData,
    setBussinessSignInFormData,
    userSignUpFormData,
    setUserSignUpFormData,
    bussinessSignUpFormData,
    setBussinessSignUpFormData,
    userSignInLoading,
    bussinessSignInLoading,
    userSignUpLoading,
    bussinessSignUpLoading,
    forgotPasswordLoading,
    handleSignInBussiness,
    handleSignInUser,
    handleSignUpBussiness,
    handleSignUpUser,
    bussinessSignUpFormControls,
    setBussinessSignUpFormControls,
  } = useContext(AuthContext)

  // logger.log('userSignInLoading ===> ', userSignInLoading)
  // logger.log('bussinessSignInLoading ===> ', bussinessSignInLoading)

  // logger.log('bussinessSignUpLoading ===> ', bussinessSignUpLoading)

  // logger.log('userSignInLoading ===> ', userSignInLoading)

  const isSpecialTab = SPECIAL_TABS.includes(activeTab)
  const isMainTab = MAIN_TABS.includes(activeTab)

  const handleTabChange = (value) => {
    if (MAIN_TABS.includes(value)) setPrevMainTab(value)
    setActiveTab(value)
  }

  const handleClose = () => {
    setIsLoginModalOpen(false)
    setAccountAlreadyCreated(false)
    setActiveTab('user')
  }

  const handleBack = () => {
    setActiveTab(prevMainTab)
    setOtp(['', '', '', ''])
  }

  const handleForgotPassword = () => {
    setPrevMainTab(activeTab)
    setActiveTab('forgot-password')
  }

  const country = bussinessSignUpFormData.country_code?.split(' ')[0]
  const getConfiguration = async () => {
    try {
      const responseData = await getConfig({
        module: 'businessDocuments',
        scope: `${country}__${bussinessSignUpFormData.business_type}`,
        key: 'requiredDocs',
      })
      setConfigValue(responseData)
      setConfig(responseData.data[0].value)
    } catch (err) {
      logger.log(err.message || 'A server error occurred. Please try again later.')
      logger.error('Error in handleSubmit (TrialModal):', err)
    }
  }

  // STEP 1: Add new field if config is valid AND data exists
  config.forEach((item) => {
    if (item && configValue?.data?.length > 0) {
      const fieldExists = bussinessSignUpFormControls.some((field) => field.name === item.key)

      if (!fieldExists) {
        const newField = {
          name: item.key,
          label: item.label,
          placeholder: 'Enter your document',
          type: 'file',
          accept: item.accept || '.jpg,.pdf,.png',
          componentType: 'input',
          className: 'border border-input-border-color px-3 h-[50px] py-4 rounded-sm',
          validations: [{ required: true, message: 'Document is required' }],
        }

        const insertIndex = Math.min(5, bussinessSignUpFormControls.length)
        const updatedControls = [...bussinessSignUpFormControls]
        updatedControls.splice(insertIndex, 0, newField)
        setBussinessSignUpFormControls(updatedControls)
        setBussinessSignUpFormData((prevData) => ({
          ...prevData,
          [item.key]: null, // Initialize new field with null
        }))
      }
    }

    // STEP 2: Remove 5th index field if no data returned
    if (configValue?.data?.length === 0 && bussinessSignUpFormControls.length > 6) {
      // Optional: check if it’s the field you added (by name)
      const fieldToRemove = bussinessSignUpFormControls[5]

      if (fieldToRemove) {
        bussinessSignUpFormControls.splice(5, 1)

        // Clean up form data too
        setBussinessSignUpFormData((prevData) => {
          const { documents, ...rest } = prevData
          if (documents) {
            const { [fieldToRemove.name]: _, ...remainingDocs } = documents
            return { ...rest, documents: remainingDocs }
          }
          return prevData
        })
      }
    }
  })

  useEffect(() => {
    getConfiguration()
  }, [bussinessSignUpFormData.country_code, bussinessSignUpFormData.business_type])

  const getFormConfig = () => {
    const configs = {
      user: {
        signIn: {
          formControls: userSignInFormControls,
          formData: userSignInFormData,
          setFormData: setUserSignInFormData,
          handleSubmit: handleSignInUser,
          isButtonDisabled: userSignInLoading,
        },
        signUp: {
          formControls: userSignUpFormControls,
          formData: userSignUpFormData,
          setFormData: setUserSignUpFormData,
          handleSubmit: handleSignUpUser,
          isButtonDisabled: userSignUpLoading,
        },
      },
      businesses: {
        signIn: {
          formControls: bussinessSignInFormControls,
          formData: bussinessSignInFormData,
          setFormData: setBussinessSignInFormData,
          handleSubmit: handleSignInBussiness,
          isButtonDisabled: bussinessSignInLoading,
        },
        signUp: {
          formControls: bussinessSignUpFormControls,
          formData: bussinessSignUpFormData,
          setFormData: setBussinessSignUpFormData,
          handleSubmit: handleSignUpBussiness,
          isButtonDisabled: bussinessSignUpLoading,
        },
      },
    }

    const mode = accountAlreadyCreated ? 'signIn' : 'signUp'
    return configs[activeTab]?.[mode]
  }

  return (
    <div className="flex flex-col lg:flex-row w-full max-w-[95vw] sm:max-w-[90vw] lg:max-w-[1200px] xl:max-w-[1400px] mx-auto h-[85vh] lg:h-[90vh] border rounded-lg overflow-hidden shadow-lg relative bg-white">
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 p-1 rounded-full bg-gray-100 hover:bg-gray-200 z-10"
        aria-label="Close modal"
      >
        <X size={20} />
      </button>

      {/* Form Section */}
      <div className="w-full lg:w-1/2 h-full overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col w-full h-full">
          {/* Header */}
          {isSpecialTab ? (
            <div className="flex items-center mb-4 py-6">
              <button
                onClick={handleBack}
                className="p-1 mr-2 rounded-full bg-gray-100 hover:bg-gray-200"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl sm:text-2xl font-extrabold">
                {activeTab === 'otp-verify' ? 'Verify OTP' : 'Forgot Password'}
              </h1>
            </div>
          ) : (
            <h1 className="text-xl sm:text-2xl py-6 font-extrabold mb-2">
              {accountAlreadyCreated ? 'Login' : 'Create Account'}
            </h1>
          )}

          {/* Special Tabs Content */}
          {isSpecialTab ? (
            <div className="w-full flex items-center justify-center  py-6 sm:py-8 mt-4 sm:mt-6">
              <div className="w-full">
                {activeTab === 'otp-verify' ? <OtpVerification /> : <ForgotPassword />}
              </div>
            </div>
          ) : (
            <>
              {/* Main Tabs */}
              <Tabs value={activeTab} defaultValue="user" onValueChange={handleTabChange}>
                <TabsList className="w-full sm:w-3/4 lg:w-full justify-start rounded-none bg-transparent p-0 gap-3">
                  {MAIN_TABS.map((tab) => (
                    <TabsTrigger
                      key={tab}
                      value={tab}
                      className="relative rounded-none border-b-2 border-b-transparent bg-transparent pr-4 pb-2 pt-1 font-semibold text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-heading-primary data-[state=active]:text-heading-primary text-sm cursor-pointer"
                    >
                      {tab === 'user' ? 'For User' : 'For Business'}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {isMainTab && (
                  <TabsContent value={activeTab} className="py-3">
                    <CommonForm
                      {...getFormConfig()}
                      buttonText={accountAlreadyCreated ? 'Login' : 'Create Account'}
                      // isButtonDisabled={false}
                      buttonClass="bg-heading-primary rounded-sm text-base px-3 py-3 sm:py-6 w-full cursor-pointer hover:bg-heading-primary-400"
                    />
                  </TabsContent>
                )}
              </Tabs>

              {/* Switch between Login and Signup */}
              <p className="mt-3 mb-3 py-3 text-sm flex items-center gap-1 text-gray-600 justify-between">
                <span>
                  {accountAlreadyCreated ? 'Not a Member?' : 'Already a Member?'}
                  <span
                    onClick={() => setAccountAlreadyCreated(!accountAlreadyCreated)}
                    className="text-heading-primary text-md ml-1 cursor-pointer"
                  >
                    {accountAlreadyCreated ? 'Sign Up' : 'Log In'}
                  </span>
                </span>

                {accountAlreadyCreated && (
                  <span
                    onClick={handleForgotPassword}
                    className="text-heading-primary hover:text-footer-border-purple ml-3 cursor-pointer"
                  >
                    Forgot Password?
                  </span>
                )}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Image Section */}
      <div className="hidden lg:flex w-1/2 lg:bg-login-page-purple lg:relative items-center justify-center">
        <img
          src="/Login.svg"
          className="w-4/5 object-contain lg:absolute top-0 right-0"
          alt="Login Visual"
        />
      </div>
    </div>
  )
}

export default AuthModal
